import React from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { ChartExportMenu } from '@/features/chart-export';
import type { ExportOptions, ChartExportOptions } from '@/shared/lib/export';
import type { ChartViewport } from '../lib/chart-options';
import { useChartViewport } from '../lib/use-chart-viewport';

interface EChartsWrapperProps {
  /**
   * Either a ready option, or a builder called with the room this chart actually
   * has. Charts whose axis labels have to fit take the builder — the window is a
   * poor proxy for a card that shares its row with another, and a zoomed-in
   * dataZoom window changes the room per category just as much as a resize does.
   */
  option: EChartsOption | ((viewport: ChartViewport) => EChartsOption);
  height?: number | string;
  className?: string;
  loading?: boolean;
  showExport?: boolean;
  exportOptions?: {
    png?: ChartExportOptions;
    csv?: ExportOptions;
    xlsx?: ExportOptions;
  };
  exportPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

const EXPORT_POSITION_CLASSES = {
  'top-left': 'absolute top-2 left-2 z-10',
  'top-right': 'absolute top-2 right-2 z-10',
  'bottom-left': 'absolute bottom-2 left-2 z-10',
  'bottom-right': 'absolute bottom-2 right-2 z-10'
} as const;

export const EChartsWrapper: React.FC<EChartsWrapperProps> = ({
  option,
  height = 400,
  className = '',
  loading = false,
  showExport = false,
  exportOptions,
  exportPosition = 'top-right'
}) => {
  const { containerRef, chartRef, resolvedOption, chartEvents } = useChartViewport(option);

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {showExport && (
        <div className={EXPORT_POSITION_CLASSES[exportPosition]}>
          <ChartExportMenu
            chartRef={chartRef}
            chartOption={resolvedOption}
            exportOptions={exportOptions}
            disabled={loading}
          />
        </div>
      )}
      <ReactECharts
        ref={chartRef}
        option={resolvedOption}
        style={{ height }}
        showLoading={loading}
        theme="light"
        opts={{ renderer: 'canvas' }}
        onEvents={chartEvents}
      />
    </div>
  );
};
